import { useEffect, useRef } from "react";
import { useInfiniteQuery } from "react-query";

import {
  OTHERS_MEMBERS_PAGE_SIZE,
  OthersMember,
  BinSearchView,
  OthersMembersRequest,
  fetchBinSearch,
  isSearchWithinLimit,
  markUnavailableOn404,
  statusOf
} from "./othersMembers";

interface UseOthersMembersQueryArgs {
  // Caller has already checked flag === true, MaxBins > 0, an Others row is
  // present, and group mode / forecast trumping / performanceDateRange are off.
  enabled: boolean;
  request: Maybe<Omit<OthersMembersRequest, "search" | "offset" | "limit" | "view">>;
  search: string;
  view?: BinSearchView;
}

interface UseOthersMembersQueryResult {
  members: OthersMember[];
  total: number;
  truncated: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isError: boolean;
  isUnavailable: boolean;
  refetch: () => void;
}

export function useOthersMembersQuery({
  enabled,
  request,
  search,
  view = "all"
}: UseOthersMembersQueryArgs): UseOthersMembersQueryResult {
  const maxBins = request?.bin?.MaxBins;
  // Only the others views need a real Max Bins; all/legend work without one.
  const needsMaxBins = view === "others";
  const queryEnabled =
    enabled &&
    !!request &&
    isSearchWithinLimit(search) &&
    (!needsMaxBins || (typeof maxBins === "number" && maxBins > 0));

  // Everything the list's identity depends on except filterId, which a check changes.
  const listKey = [
    request?.property,
    request?.bin,
    request?.entityKind,
    request?.pdenSource,
    request?.normalizeBySetting,
    request?.showGroupsNotInFilter,
    request?.shapefileMatchMode,
    request?.sortBy,
    search,
    view
  ];
  const listKeyJson = JSON.stringify(listKey);
  // react-query v3 keeps the previous key's data for every render while the new key loads,
  // so compare against the list whose own data last landed: only a check's new filterId
  // may show the old rows meanwhile, never another property's or search's members.
  const settledListKeyRef = useRef(listKeyJson);
  const keepPreviousData = view === "others" && settledListKeyRef.current === listKeyJson;

  const query = useInfiniteQuery(
    ["othersMembers", request?.filterId, ...listKey],
    ({ pageParam = 0, signal }) =>
      fetchBinSearch(
        { ...request, view, search, offset: pageParam, limit: OTHERS_MEMBERS_PAGE_SIZE },
        signal
      ),
    {
      enabled: queryEnabled,
      staleTime: 60_000,
      keepPreviousData,
      getNextPageParam: (lastPage, allPages) => {
        // `total` is the full collapsed-group count, which can exceed the
        // backend's 100,000-member capture cap when truncated -- once paging
        // has walked past the capture, every further page comes back with
        // fewer than a full page (down to 0) of members while `total` still
        // reads higher, so comparing loadedCount to total alone would ask
        // for another empty page forever. A short page is the actual
        // end-of-data signal, truncated or not.
        if (lastPage.members.length < OTHERS_MEMBERS_PAGE_SIZE) return undefined;
        const loadedCount = allPages.reduce((sum, page) => sum + page.members.length, 0);
        return loadedCount < lastPage.total ? loadedCount : undefined;
      },
      // No retry on any status: 400/404/410 must never retry (contract), and
      // there is no requirement to retry a 5xx here either — the section's
      // own "Retry" control (OthersMembersExpansion) is the retry mechanism.
      retry: false
    }
  );

  useEffect(() => {
    if (query.isSuccess && !query.isPreviousData) settledListKeyRef.current = listKeyJson;
  }, [query.isSuccess, query.isPreviousData, listKeyJson]);

  const status = statusOf(query.error);
  const isUnavailable = query.isError && status === 404;
  const isError = query.isError && status !== 404;

  useEffect(() => {
    markUnavailableOn404(query.error);
  }, [query.error]);

  return {
    members: query.data?.pages.flatMap((page) => page.members) ?? [],
    total: query.data?.pages[query.data.pages.length - 1]?.total ?? 0,
    // The server-side 100,000-member capture cap, not a paging concept --
    // every page shares the same value, so the latest page's is authoritative.
    truncated: query.data?.pages[query.data.pages.length - 1]?.truncated ?? false,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => {
      query.fetchNextPage();
    },
    isError,
    isUnavailable,
    refetch: () => {
      query.refetch();
    }
  };
}
